import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { map, catchError, finalize, shareReplay } from 'rxjs/operators';
import { isAdminRole, isSuperAdminRole } from '../utils/user-roles';
import { ApiService } from './api.service';

export interface User {
  userId: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'superadmin' | 'instructor';
  mobilePhone?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  emailOrUsername: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    refreshToken: string;
    user: User;
    expiresAt: string;
  } | null;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  expiresAt: Date | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentAuthStateSubject = new BehaviorSubject<AuthState>({
    user: null,
    token: null,
    refreshToken: null,
    expiresAt: null
  });

  public currentAuthState$ = this.currentAuthStateSubject.asObservable();
  public currentUser$ = this.currentAuthState$.pipe(
    map(authState => authState.user)
  );

  private refreshInFlight$: Observable<string> | null = null;

  constructor(private api: ApiService) {
    this.loadStoredAuthState();
  }

  /**
   * Restore tokens from localStorage only. Do not refresh here — APP_INITIALIZER /
   * AuthGuard / interceptor call ensureValidSession / refreshToken when needed.
   */
  private loadStoredAuthState(): void {
    const storedToken = localStorage.getItem('auth_token');
    const storedRefreshToken = localStorage.getItem('refresh_token');
    const storedUser = localStorage.getItem('current_user');
    const storedExpiresAt = localStorage.getItem('token_expires_at');

    if (!storedUser) {
      return;
    }

    try {
      const user: User = JSON.parse(storedUser);
      const expiresAt = storedExpiresAt ? new Date(storedExpiresAt) : new Date(0);

      if (storedRefreshToken || (storedToken && expiresAt > new Date())) {
        this.setAuthState(
          user,
          storedToken || '',
          storedRefreshToken,
          expiresAt
        );
      } else {
        this.clearStoredAuthState();
      }
    } catch {
      this.clearStoredAuthState();
    }
  }

  login(emailOrUsername: string, password: string): Observable<LoginResponse> {
    const loginRequest: LoginRequest = {
      emailOrUsername,
      password
    };

    return this.api.postPath<LoginResponse>('auth/admin-login', loginRequest)
      .pipe(
        map(response => {
          if (response.success && response.data) {
            const { user, token, refreshToken, expiresAt } = response.data;
            this.setAuthState(user, token, refreshToken, new Date(expiresAt));
          }
          return response;
        }),
        catchError(this.handleAuthError)
      );
  }

  logout(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    this.clearAuthState();

    if (!refreshToken) {
      return of({ success: true });
    }

    return this.api.postPath('auth/logout', { refreshToken }).pipe(
      catchError(() => of({ success: true })),
      map(() => ({ success: true }))
    );
  }

  /**
   * Refresh access token using stored refresh token.
   * Single-flight: concurrent callers share one in-flight request.
   */
  refreshToken(): Observable<string> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    this.refreshInFlight$ = this.api
      .postPath<LoginResponse>('auth/refresh', { refreshToken })
      .pipe(
        map(response => {
          if (!response.success || !response.data) {
            throw new Error(response.message || 'Token refresh failed');
          }
          const { user, token, refreshToken: newRefresh, expiresAt } = response.data;
          this.setAuthState(user, token, newRefresh, new Date(expiresAt));
          return token;
        }),
        catchError(err => {
          this.clearAuthState();
          return throwError(() => err);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.refreshInFlight$;
  }

  getCurrentUser(): User | null {
    return this.currentAuthStateSubject.value.user;
  }

  getToken(): string | null {
    const token = this.currentAuthStateSubject.value.token;
    return token || null;
  }

  getRefreshToken(): string | null {
    return this.currentAuthStateSubject.value.refreshToken
      || localStorage.getItem('refresh_token');
  }

  isAuthenticated(): boolean {
    const authState = this.currentAuthStateSubject.value;
    if (!authState.user) {
      return false;
    }
    if (authState.token && authState.expiresAt && authState.expiresAt > new Date()) {
      return true;
    }
    return !!this.getRefreshToken();
  }

  isTokenExpired(): boolean {
    const expiresAt = this.currentAuthStateSubject.value.expiresAt;
    return !expiresAt || expiresAt <= new Date();
  }

  /**
   * Ensures a usable access token before the app/route loads data.
   * Returns false when the session cannot be renewed (caller should send user to login).
   */
  ensureValidSession(): Observable<boolean> {
    const hasUser = !!this.currentAuthStateSubject.value.user;
    const hasRefresh = !!this.getRefreshToken();

    if (!hasUser && !hasRefresh) {
      return of(false);
    }

    if (!this.isTokenExpired() && this.getToken()) {
      return of(true);
    }

    if (!hasRefresh) {
      this.clearAuthState();
      return of(false);
    }

    return this.refreshToken().pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return isAdminRole(user?.role);
  }

  isSuperAdmin(): boolean {
    const user = this.getCurrentUser();
    return isSuperAdminRole(user?.role);
  }

  isInstructor(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'instructor';
  }

  private setAuthState(
    user: User,
    token: string,
    refreshToken: string | null,
    expiresAt: Date
  ): void {
    localStorage.setItem('current_user', JSON.stringify(user));
    localStorage.setItem('auth_token', token);
    localStorage.setItem('token_expires_at', expiresAt.toISOString());
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }

    this.currentAuthStateSubject.next({
      user,
      token,
      refreshToken,
      expiresAt
    });
  }

  private clearAuthState(): void {
    this.clearStoredAuthState();
    this.currentAuthStateSubject.next({
      user: null,
      token: null,
      refreshToken: null,
      expiresAt: null
    });
  }

  private clearStoredAuthState(): void {
    localStorage.removeItem('current_user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
  }

  private handleAuthError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'An authentication error occurred';

    if (error.error && typeof error.error === 'object' && error.error.message) {
      errorMessage = error.error.message;
    } else if (error.error && typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.status === 0) {
      errorMessage = 'Unable to connect to server';
    } else if (error.statusText) {
      errorMessage = error.statusText;
    }

    console.error('Authentication error:', error);
    return throwError(() => ({ message: errorMessage }));
  };
}
